import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output,
	ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Device, DeviceDataRequest } from 'src/app/models/data/device';
import { ApiLoadingKeys, ApiService } from 'src/app/services/api.services';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { EditDeviceGroup } from '../add-device-group-main-tab.component';
import { NotAuthorizedNoticeComponent } from '../../../components/not-authorized-notice/not-authorized-notice.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { OverflowChipListComponent } from '../../../components/overflow-chip-list/overflow-chip-list.component';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';
@Component({
	selector: 'app-add-device-group-devices',
	templateUrl: './add-device-group-devices.component.html',
	styleUrls: [
		'./add-device-group-devices.component.scss',
		'../../common/main.scss',
		'../../common/stepper.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		DataSetComponent_1,
		HasSystemCapabilityDirective,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		MatSortHeader,
		OverflowChipListComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DataSetLoadingRowComponent,
	],
})
export class AddDeviceGroupDevicesComponent
	implements AfterViewInit, OnDestroy
{
	@Input() deviceDataRequest!: DeviceDataRequest;
	@Output() devicesSelectedEvents: EventEmitter<string[]> = new EventEmitter();

	@Input() editDeviceGroup!: EditDeviceGroup;
	/** Loading key for the device list request; defaults to getAllDevices. */
	@Input() devicesLoadingKey: string = ApiLoadingKeys.getAllDevices;

	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	displayedColumns: string[] = [
		'rowCheckbox',
		'serialNumber',
		'propertyName',
		'deviceLocation',
		'group',
		'status',
	];
	dataSource = new MatTableDataSource<{
		device: Device;
		groupNames?: string[];
	}>();
	private filterSubscription?: Subscription;

	selectionChangedSubscription?: Subscription;

	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	deviceGroupId: string;

	filterLabels = {
		defaultPlaceholder: $localize`:@@addDeviceGroupDevices-defaultPlaceholder:Search serial number...`,
		serialNumber: $localize`:@@addDeviceGroupDevices-filterLabelSerialNumber:Serial Number`,
		deviceGroups: $localize`:@@addDeviceGroupDevices-filterLabelDeviceGroups:Device Group(s)`,
		status: $localize`:@@addDeviceGroupDevices-filterLabelStatus:Status`,
		propertyName: $localize`:@@addDeviceGroupDevices-filterLabelPropertyName:Property Name`,
		streetAddress: $localize`:@@addDeviceGroupDevices-filterLabelStreetAddress:Street Address`,
		groups: $localize`:@@addDeviceGroupDevices-filterLabelGroups:Group(s)`,
		country: $localize`:@@addDeviceGroupDevices-filterLabelCountry:Country`,
		address: $localize`:@@addDeviceGroupDevices-filterLabelAddress:Address`,
		townCity: $localize`:@@addDeviceGroupDevices-filterLabelTownCity:Town/City`,
		stateProvince: $localize`:@@addDeviceGroupDevices-filterLabelStateProvince:State/Province`,
		stateProvinceCounty: $localize`:@@addDeviceGroupDevices-filterLabelStateProvinceCounty:State/Province`,
		postalCode: $localize`:@@addDeviceGroupDevices-filterLabelPostalCode:Postal Code`,
	};

	finishedLoadingInitData = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly navHistory: NavigationHistoryService,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.deviceGroupId = route.snapshot.params.id;
	}

	async ngAfterViewInit(): Promise<void> {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				switchMap(async (filter) => {
					const result = await this.deviceDataRequest(filter);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
			});

		this.selectionChangedSubscription =
			this.dataSet.selection.changed.subscribe(() => {
				this.editDeviceGroup.deviceIds = this.dataSet?.selection.selected.map(
					(device) => device.device.id
				);
			});
	}

	get isValid(): boolean {
		return this.editDeviceGroup.deviceIds.length > 0;
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;

		this.selectionChangedSubscription?.unsubscribe();
		this.selectionChangedSubscription = undefined;
	}

	rowId(row: { device: Device; groupNames?: string[] }): string {
		return row?.device?.id || 'unknown-id';
	}
}
